import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * Fetches all data needed for the landing page
 * @returns {Promise<Object>} Object containing slides, services, works, and clients
 */
export async function fetchHomeData() {
  try {
    const response = await apiClient.get("/api/home");

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch home data");
    }
  } catch (error) {
    console.error("Error fetching home data:", error);
    throw error;
  }
}

export async function fetchServicesData(count = true) {
  try {
    const response = await apiClient.get("/api/services/public", {
      params: { count },
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch services data");
    }
  } catch (error) {
    console.error("Error fetching services data:", error);
    throw error;
  }
}

export async function fetchContactData(count = true) {
  try {
    const response = await apiClient.get("/api/contact/public", {
      params: { count },
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch contact data");
    }
  } catch (error) {
    console.error("Error fetching contact data:", error);
    throw error;
  }
}

/**
 * Fetches all data needed for the company page
 * @returns {Promise<Object>} Object containing services, clients, and teams
 */
export async function fetchCompanyData() {
  try {
    const response = await apiClient.get("/api/company");

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch company data");
    }
  } catch (error) {
    console.error("Error fetching company data:", error);
    throw error;
  }
}

/**
 * Generic fetch wrapper for other API calls
 * @param {string} endpoint - API endpoint path (e.g., '/api/works')
 * @param {Object} options - Axios options (method, params, data, etc.)
 * @returns {Promise<any>} Response data
 */
export async function fetchAPI(endpoint, options = {}) {
  try {
    const response = await apiClient.request({
      url: endpoint,
      ...options,
    });

    return response.data;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Fetches filter options for works (categories, clients, industries)
 * @returns {Promise<Object>} Object containing categories, clients, and industries
 */
export async function fetchWorksFilters() {
  try {
    const response = await apiClient.get("/api/works/filters");

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch filters");
    }
  } catch (error) {
    console.error("Error fetching works filters:", error);
    throw error;
  }
}

/**
 * Fetches paginated works with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.category_id - Filter by category ID
 * @param {number} params.client_id - Filter by client ID
 * @param {number} params.industry_id - Filter by industry ID
 * @returns {Promise<Object>} Object containing works data and pagination meta
 */
export async function fetchWorks(params = {}) {
  try {
    const response = await apiClient.get("/api/works/public", { params });

    if (response.data.success) {
      return {
        data: response.data.data,
        meta: response.data.meta,
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch works");
    }
  } catch (error) {
    console.error("Error fetching works:", error);
    throw error;
  }
}

/**
 * Fetches all active services
 * @returns {Promise<Array>} Array of service objects
 */
export async function fetchServices() {
  try {
    const response = await apiClient.get("/api/works/filters");

    if (response.data.success) {
      return response.data.data.services || [];
    } else {
      throw new Error(response.data.message || "Failed to fetch services");
    }
  } catch (error) {
    console.error("Error fetching services:", error);
    throw error;
  }
}

export async function fetchWorkSlides() {
  try {
    const response = await apiClient.get("/api/works/slides");

    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.message || "Failed to fetch services");
    }
  } catch (error) {
    console.error("Error fetching services:", error);
    throw error;
  }
}

/**
 * Fetches a single work detail by slug
 * @param {string} slug - Work slug
 * @returns {Promise<Object>} Work detail object
 */
export async function fetchWorkDetail(slug) {
  try {
    const response = await apiClient.get(`/api/works/public/${slug}`);

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch work detail");
    }
  } catch (error) {
    console.error("Error fetching work detail:", error);
    throw error;
  }
}
export default apiClient;
